<template>
  <div id="upload-frames-container">
    <div
      v-if="!isNoMetadataUploadMode && !isUploadUpdateMode"
      class="upload-container"
    >
      <h6>{{ t('documentUpload.importDataSets') }}</h6>
      <upload-box
        :accept-extensions="FileExtension.CSV"
        max-size="4096"
        :button-label="t('documentUpload.csvImport')"
        :disabled="isCsvInputDisabled"
        :reset-input-on-button-click="true"
        :upload-progress="uploadProgress"
        @change="handleCsvUpload"
      />
    </div>

    <div v-if="!isUploadUpdateMode" class="upload-container">
      <h6>{{ t('documentUpload.uploadPackage') }}</h6>
      <upload-box
        :accept-extensions="FileExtension.ZIP"
        :button-label="t('documentUpload.uploadZipFile')"
        :disabled="isNoMetadataUploadMode && areNoMetadataFileInputsDisabled"
        :upload-box-mode="UploadBoxMode.FILE"
        :upload-progress="uploadProgress"
        max-size="4096"
        @change="readZip"
      />
    </div>

    <div v-if="isNoMetadataUploadMode" class="upload-container">
      <h6>{{ t('documentUpload.uploadDirectory') }}</h6>
      <upload-box
        :button-label="t('documentUpload.uploadDirectory')"
        :disabled="areNoMetadataFileInputsDisabled"
        :reset-input-on-button-click="true"
        :upload-box-mode="UploadBoxMode.DIRECTORY"
        :upload-progress="uploadProgress"
        max-size="4096"
        @change="handleMultiFileUpload"
      />
    </div>

    <div
      v-if="isNoMetadataUploadMode || isUploadUpdateMode"
      class="upload-container"
    >
      <h6>{{ t('documentUpload.uploadMultipleFiles') }}</h6>
      <upload-box
        :button-label="t('documentUpload.uploadMultipleFiles')"
        :disabled="areNoMetadataFileInputsDisabled"
        :reset-input-on-button-click="true"
        :upload-box-mode="UploadBoxMode.MULTIPLE_FILES"
        :upload-progress="uploadProgress"
        max-size="4096"
        @change="handleMultiFileUpload"
      />
    </div>

    <csv-parsing-errors-modal
      ref="modalRef"
      :csv-parsing-errors="csvParsingErrors"
    ></csv-parsing-errors-modal>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, onBeforeUnmount } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import CsvParsingErrorsModal from '@/components/CsvParsingErrorsModal/CsvParsingErrorsModal.vue';
  import iconv from 'iconv-lite';
  import JSZip from 'jszip';
  import Papa, { ParseError } from 'papaparse';
  import { isDefined, isSystemPath } from 'utils';
  import { FileExtension, UploadBoxMode } from '../UploadBox/UploadBox.types';
  import UploadBox from '../UploadBox/UploadBox.vue';
  import { UploadType } from '../MassUploadTable/MassUploadTable.types';
  import { IUploadRecord } from '@/components/MassUpload/MassUpload.types';

  interface IFileAndFilepath {
    file: File;
    path: string;
  }

  interface ICsvDataSet {
    [key: string]: string | number;
  }

  const { t } = useI18n();
  const { createErrorToast } = useToast();

  const csvParsingErrors = ref<ParseError[] | undefined>(undefined);
  const emit = defineEmits<{
    (e: 'mass-upload-records', payload: any): void;
    (e: 'mass-upload-files', payload: any): void;
    (e: 'reading-zip-file'): void;
    (e: 'zip-reading-error'): void;
  }>();

  const props = defineProps<{
    areNoMetadataFileInputsDisabled?: boolean;
    isCsvInputDisabled?: boolean;
    isNoMetadataUploadMode?: boolean;
    isUploadUpdateMode?: boolean;
    uploadProgress?: number;
  }>();

  function handleCsvUpload(files: File[]) {
    const file = files[0];
    if (!file) return;
    parseCsv(file);
  }

  const modalRef = ref<InstanceType<typeof CsvParsingErrorsModal> | null>(null);

  function parseCsv(file: File) {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      encoding: 'iso-8859-1',
      transform(value) {
        return value.trim();
      },
      complete: (results) => {
        if (results.errors.length) {
          csvParsingErrors.value = results.errors;
          modalRef.value?.show();

          createErrorToast(t('documentUpload.errorReadingCsv'));

          return;
        }

        if (!results?.data) {
          return;
        }

        const data = results.data as ICsvDataSet[];
        const filteredData = data.filter(
          (dataSet) => !Object.values(dataSet).every((value) => value === null),
        );
        const records = transformCsvDataToUploadRecords(filteredData);

        emit('mass-upload-records', records);
      },
    });
  }

  function transformCsvDataToUploadRecords(
    csvData: ICsvDataSet[],
  ): IUploadRecord[] {
    const records: IUploadRecord[] = csvData.map((dataSet) => {
      const dataSetWithStringifiedValues =
        transformDataSetValuesToStrings(dataSet);

      const record: IUploadRecord = {
        uploadType: UploadType.NEW_DOCUMENT,
        rawMetadata: dataSetWithStringifiedValues,
      };

      return record;
    });

    return records;
  }

  function buildMockUploadRecords(files: IFileAndFilepath[]): IUploadRecord[] {
    return files.map((file) => ({
      file: file.file,
      uploadType: UploadType.NEW_DOCUMENT,
      rawMetadata: {},
    }));
  }

  function transformDataSetValuesToStrings(
    dataSet: ICsvDataSet,
  ): Record<string, string> {
    const entriesWithStringValues = Object.entries(dataSet).map((data) => [
      data[0],
      data[1]?.toString() ?? '',
    ]);

    return Object.fromEntries(entriesWithStringValues);
  }

  async function readZip(files: File[]) {
    emit('reading-zip-file');
    const file = files[0];
    if (!file) return;
    try {
      const zip = await JSZip.loadAsync(file, {
        decodeFileName: (bytes) => iconv.decode(bytes as any, 'cp850'),
      });
      const filePromises = Object.keys(zip.files).map(async (fileName) => {
        const entry = zip.files[fileName];
        if (!entry.dir) {
          const blob = await entry.async('blob');
          const file = new File(
            [blob],
            fileName.split('/')[fileName.split('/').length - 1],
          );
          return { file, path: fileName };
        }
      });

      const files: IFileAndFilepath[] = (await Promise.all(filePromises))
        .filter(isDefined)
        .filter((file) => !isSystemPath(file.path));

      if (props.isNoMetadataUploadMode) {
        const mockedUploadRecords = buildMockUploadRecords(files);
        emit('mass-upload-records', mockedUploadRecords);
      }

      emit('mass-upload-files', files);
    } catch {
      emit('zip-reading-error');

      createErrorToast(t('documentUpload.errors.zipCorrupted'));
    }
  }

  function handleMultiFileUpload(files: File[]) {
    const filteredFiles = files.filter((file) => !isSystemPath(file.name));
    if (!filteredFiles.length) return;
    const mockedUploadRecords = buildMockUploadRecords(
      filteredFiles.map((file) => ({ file, path: file.name })),
    );
    emit('mass-upload-records', mockedUploadRecords);
  }

  function preventDragDefaults(e: any) {
    e.preventDefault();
    e.stopPropagation();
  }

  onMounted(() => {
    window.addEventListener('dragover', preventDragDefaults);
    window.addEventListener('drop', preventDragDefaults);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('dragover', preventDragDefaults);
    window.removeEventListener('drop', preventDragDefaults);
  });
</script>

<style scoped lang="scss">
  #upload-frames-container {
    width: 100%;
    display: flex;
    column-gap: 35px;

    .upload-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      h6 {
        font-size: 20px;
        font-weight: 700;
      }
    }
  }
</style>
